import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Typography,
  Button,
  makeStyles,
  IconButton,
  Divider,
  TextField,
  Box,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";

import exerciseConstants from "constants/Exercises/exerciseConstants";
import TaskResponse from "./TaskResponse";
import DOT from "components/MachineElement/DOT";

import { useDot } from "context/automatonContext";
import { useGrammar } from "context/grammarContext";
import { useUser } from "context/userContext";

import { useSimulationRedirect } from "../hooks/useSimulationRedirect";
import DataService from "services/DataService";
import { MACHINE_TYPES } from "variables/machineVariables";

/**
 * Estilos do componente HUD
 */
const useStyles = makeStyles((theme) => ({
  hud: {
    width: 320,
    padding: theme.spacing(2),
    backgroundColor: "white",
    borderTop: "6px solid #3f51b5",
    boxShadow: "0px 10px 30px rgba(0,0,0,0.3)",
    borderRadius: "8px",
  },
  titleArea: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  descriptionText: {
    color: "#222",
    fontWeight: 500,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    maxHeight: "150px",
    overflowY: "auto",
  },
  regexInput: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f4f6f8",
    },
  },
}));

/**
 * @typedef {Object} Task
 * @property {string} type Tipo da tarefa (ex: REGULAR_EXPRESSIONS, GRAMMAR_*)
 * @property {string|Object} description Descrição da tarefa (string ou multi-idioma)
 */

/**
 * HUD (Heads-Up Display) para execução de tarefas interativas.
 *
 * Responsabilidades:
 * - Exibir descrição da tarefa
 * - Permitir entrada de dados (ex: regex)
 * - Enviar resposta ao backend
 * - Simular palavras (gramática/autômatos/regex)
 * - Persistir estado da resposta
 * - Redirecionar para visualização de simulação
 *
 * @component
 *
 * @param {Object} props
 * @param {Task|null} props.task Tarefa ativa
 * @param {(value?: string) => Promise<any>} props.onSend Função de envio da resposta
 * @param {() => void} props.onClose Callback de fechamento
 * @param {"pt"|"en"} props.language Idioma atual
 *
 * @returns {JSX.Element|null}
 */
export default function ExerciseTaskHud({ task, onSend, onClose, language }) {
  const classes = useStyles();
  const { handleRedirect } = useSimulationRedirect();
  const isFirstRender = useRef(true);

  const {
    machineStates,
    machineTransitions,
    automatonType,
    setIsSimulating,
    setIsMultiPathSimulating,
    setSimulationIndex,
    setDot,
    setMachineStates,
    setMachineTransitions,
    setSimulationPath,
    setDotName,
  } = useDot();

  const { originalGrammar, setOriginalGrammar, setGrammarName } = useGrammar();
  const { credential } = useUser();

  const hudLabels = exerciseConstants.hud;
  const formLabels = exerciseConstants.form.labels;
  const placeholders = exerciseConstants.form.placeholders;

  /** Símbolo lambda (ε) */
  const LAMBDA_SYMBOL = "λ";
  /** Gatilho para entrada vazia */
  const EMPTY_INPUT_TRIGGER = ".";

  // =========================
  // STORAGE
  // =========================

  /**
   * Recupera resposta previamente salva no sessionStorage
   *
   * @returns {Object|null}
   */
  const getStoredResponse = () => {
    try {
      const savedResponse = sessionStorage.getItem("active_grammar_response");
      const savedTask = sessionStorage.getItem("active_grammar_task");

      if (savedTask && task && savedTask === JSON.stringify(task)) {
        return savedResponse ? JSON.parse(savedResponse) : null;
      }
    } catch (e) {
      console.error("Erro ao recuperar response", e);
    }
    return null;
  };

  const [backendResponse, setBackendResponse] = useState(getStoredResponse);
  const [regexValue, setRegexValue] = useState("");

  // =========================
  // EFFECT
  // =========================

  /**
   * Reseta estado ao trocar de tarefa
   */
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setBackendResponse(null);
    setRegexValue("");
    sessionStorage.removeItem("active_grammar_response");
    sessionStorage.removeItem("active_grammar_task");
  }, [JSON.stringify(task)]);

  // =========================
  // HELPERS
  // =========================

  /**
   * Substitui "." por símbolo λ
   *
   * @param {string} value
   * @returns {string}
   */
  const handleLambdaReplacement = (value) => {
    if (value.includes(EMPTY_INPUT_TRIGGER)) {
      return value.split(EMPTY_INPUT_TRIGGER).join(LAMBDA_SYMBOL);
    }
    return value;
  };

  /**
   * Atualiza valor da regex com tratamento de λ
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleRegexChange = (e) => {
    const value = handleLambdaReplacement(e.target.value);
    setRegexValue(value);
  };

  /**
   * Resolve palavra clicada para envio ao backend
   *
   * @param {string} word
   * @returns {string}
   */
  const resolveWord = (word) => {
    if (word === LAMBDA_SYMBOL || word === "") {
      return " ";
    }
    return word;
  };

  /**
   * Reseta estado de simulação do autômato
   */
  const resetSimulationState = () => {
    setIsSimulating(false);
    setIsMultiPathSimulating(false);
    setSimulationIndex(0);
  };

  /**
   * Gera código DOT do autômato atual
   *
   * @returns {string}
   */
  const generateDot = () => {
    const dot = new DOT(machineStates, machineTransitions);
    dot.toDotLanguage();
    return dot.toString();
  };

  /**
   * Obtém descrição da tarefa considerando idioma
   *
   * @returns {string}
   */
  const getDescription = () => {
    if (!task?.description) {
      return "";
    }

    if (typeof task.description === "object") {
      return task.description[language];
    }

    return task.description;
  };

  // =========================
  // TASK SEND
  // =========================

  /**
   * Executa envio da resposta baseado no tipo da tarefa
   *
   * @async
   * @returns {Promise<any>}
   */
  const executeTaskSend = async () => {
    const handlers = {
      REGULAR_EXPRESSIONS: () => onSend(regexValue),
      DEFAULT: () => onSend(),
    };

    const handler = handlers[task.type] || handlers.DEFAULT;
    return await handler();
  };

  /**
   * Handler principal de envio da resposta
   */
  const handleAction = async () => {
    if (!onSend) {
      return;
    }

    try {
      const response = await executeTaskSend();
      if (!response) {
        return;
      }
      setBackendResponse(response);
      sessionStorage.setItem(
        "active_grammar_response",
        JSON.stringify(response)
      );
      sessionStorage.setItem("active_grammar_task", JSON.stringify(task));
    } catch (err) {
      console.error("Erro ao enviar resposta:", err);
    }
  };

  // =========================
  // WORD CHECK
  // =========================

  /**
   * Executa verificação de palavra no backend
   *
   * @param {string} word Palavra a ser testada
   * @param {string|null} dotCode Código DOT do autômato (ou null para gramática)
   * @param {Object} config Configuração HTTP (headers)
   *
   * @returns {Promise<any>}
   */
  const executeWordCheck = async (word, dotCode, config) => {
    // GRAMÁTICA
    if (task.type.includes("GRAMMAR")) {
      const body = { variables: originalGrammar };
      return await DataService.checkGrammarWord(body, word, config);
    }
    // AUTÔMATOS
    const machineHandlers = {
      [MACHINE_TYPES.AP]: () =>
        DataService.checkPDAWord(dotCode, word, "DEFAULT", config),

      [MACHINE_TYPES.ALL]: () =>
        DataService.checkLBAWord(dotCode, word, config),

      [MACHINE_TYPES.MT]: () => DataService.checkTMWord(dotCode, word, config),

      DEFAULT: () => DataService.checkWord(dotCode, word, config),
    };

    const handler = machineHandlers[automatonType] || machineHandlers.DEFAULT;
    return await handler();
  };

  /**
   * Redireciona para visualização da simulação
   *
   * @param {any} response Resposta do backend
   * @param {string|null} dotCode Código DOT do autômato
   */
  const handleSimulationRedirect = (response, dotCode) => {
    if (!response) {
      return;
    }

    if (task.type.includes("GRAMMAR")) {
      sessionStorage.setItem("active_grammar_task", JSON.stringify(task));
      handleRedirect(response.data, null, "GRAMMAR", task);
      return;
    }

    handleRedirect(response, dotCode, "MACHINE");
  };

  /**
   * Handler de clique em palavra (suporta REGEX, GRAMMAR e MACHINE)
   *
   * @param {string} word
   */
  const handleWordClick = async (word) => {
    const wordToSend = resolveWord(word);
    resetSimulationState();

    try {
      const config = { headers: { Authorization: credential } };

      if (task.type === "REGULAR_EXPRESSIONS") {
        if (!regexValue || regexValue.trim() === "") {
          console.warn("Regex não informado");
          return;
        }
        const automatonResponse = await DataService.getAutomatonByRegex(
          regexValue,
          config
        );
        const automatonDot = automatonResponse.data;
        setDot(automatonDot);
        const response = await DataService.checkWord(
          automatonDot,
          wordToSend,
          config
        );
        handleRedirect(response, automatonDot, "MACHINE");
        return;
      }

      const dotCode = task.type.includes("GRAMMAR") ? null : generateDot();
      const response = await executeWordCheck(wordToSend, dotCode, config);

      handleSimulationRedirect(response, dotCode);
    } catch (error) {
      console.error("Erro na simulação via HUD:", error);
    }
  };

  /**
   * Fecha HUD e limpa estado
   */
  const handleCloseHud = () => {
    setBackendResponse(null);
    setRegexValue("");
    setDot(null);
    setDotName(null);
    setOriginalGrammar("");
    setGrammarName(null);
    setMachineStates([]);
    setMachineTransitions([]);
    setSimulationPath([]);
    setIsSimulating(false);
    setIsMultiPathSimulating(false);
    setSimulationIndex(0);

    // limpar storage correto
    sessionStorage.removeItem("active_grammar_response");
    sessionStorage.removeItem("active_grammar_task");

    onClose?.();
  };

  if (!task) {
    return null;
  }

  const description = getDescription();

  return (
    <Paper className={classes.hud} elevation={10}>
      <div className={classes.titleArea}>
        <Typography
          variant="overline"
          style={{ fontWeight: 800, color: "#3f51b5" }}
        >
          {hudLabels.activeChallenge[language]}
        </Typography>

        <IconButton size="small" onClick={handleCloseHud}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <Divider />

      <Typography variant="body2" className={classes.descriptionText}>
        {description}
      </Typography>

      {task.type === "REGULAR_EXPRESSIONS" && (
        <Box className={classes.regexInput}>
          <TextField
            fullWidth
            variant="outlined"
            label={formLabels.regex[language]}
            placeholder={placeholders.regex[language]}
            value={regexValue}
            onChange={handleRegexChange}
            size="small"
          />
        </Box>
      )}

      <TaskResponse
        responseData={backendResponse}
        language={language}
        onWordClick={handleWordClick}
      />

      <Button
        variant="contained"
        fullWidth
        startIcon={<SendIcon />}
        onClick={handleAction}
        color="primary"
        style={{ fontWeight: "bold", marginTop: 8 }}
      >
        {hudLabels.submit[language]}
      </Button>
    </Paper>
  );
}
